import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import WeChatLogin from './WeChatLogin';
import { openAppDownloadQr } from '../appUpdate';

const REMEMBER_KEY = 'xbb_remember_credentials';

const Login: React.FC = () => {
  const { login, register, loginWithTokenAndUser } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [apkDownloadMessage, setApkDownloadMessage] = useState('');
  const [apkDownloadBusy, setApkDownloadBusy] = useState(false);
  // 启动时读取已保存的凭据
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        const { username: savedUser, password: savedPass } = JSON.parse(saved);
        setUsername(savedUser || '');
        setPassword(savedPass || '');
        setRememberMe(true);
      }
    } catch {
      // ignore
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) { setError('请填写用户名和密码'); return; }
    if (isRegister && password !== confirmPassword) { setError('两次密码不一致'); return; }
    if (isRegister && password.length < 6) { setError('密码至少6位'); return; }

    setLoading(true);
    setError('');
    try {
      if (isRegister) {
        await register(username.trim(), password, nickname.trim() || undefined);
      } else {
        await login(username.trim(), password);
        // 登录成功后处理记住密码
        if (rememberMe) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username: username.trim(), password }));
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || err.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenApkDownload = async () => {
    setApkDownloadBusy(true);
    setApkDownloadMessage('');
    try {
      await openAppDownloadQr();
    } catch (err: any) {
      setApkDownloadMessage(err?.message || '打开下载二维码失败，请稍后重试');
    } finally {
      setApkDownloadBusy(false);
    }
  };

  return (
    <div className="login-page">
      {/* 背景星尘粒子 */}
      <div className="login-stars">
        {Array.from({ length: 30 }).map((_, i) => (
          <div key={i} className="star" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${2 + Math.random() * 3}s`,
            width: `${1 + Math.random() * 3}px`,
            height: `${1 + Math.random() * 3}px`,
          }} />
        ))}
      </div>

      {/* 背景装饰 */}
      <div className="login-bg-orb orb-1" />
      <div className="login-bg-orb orb-2" />
      <div className="login-bg-orb orb-3" />

      <div className="login-container">
        {/* Logo */}
        <div className="login-logo">
          <div className="login-logo-icon">
            <img src={`${process.env.PUBLIC_URL}/favicon.svg`} width="72" height="72" alt="" />
          </div>
          <h1 className="login-brand">小本本</h1>
          <p className="login-tagline">Xiao Ben Ben · 人生管理系统</p>
        </div>

        {/* 表单卡片 */}
        <div className="login-card">
          <h2 className="login-title">{isRegister ? '创建账号' : '欢迎回来'}</h2>
          <p className="login-desc">{isRegister ? '开始记录你的生活' : '继续管理你的人生'}</p>

          {error && <div className="login-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label>用户名</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="输入用户名" autoComplete="username" />
            </div>

            {isRegister && (
              <div className="login-field">
                <label>昵称 <span className="optional">(选填)</span></label>
                <input type="text" value={nickname} onChange={e => setNickname(e.target.value)} placeholder="你的称号" />
              </div>
            )}

            <div className="login-field">
              <label>密码</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="输入密码" autoComplete={isRegister ? 'new-password' : 'current-password'} />
            </div>

            {isRegister && (
              <div className="login-field">
                <label>确认密码</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="再次输入密码" autoComplete="new-password" />
              </div>
            )}

            {/* 记住密码 - 仅在登录模式显示 */}
            {!isRegister && (
              <div className="login-remember">
                <label className="remember-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    className="remember-checkbox"
                  />
                  <span className="remember-custom-box">
                    {rememberMe && (
                      <svg viewBox="0 0 12 12" width="10" height="10">
                        <polyline points="2,6 5,9 10,3" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                  <span className="remember-text">记住用户名和密码</span>
                </label>
                {rememberMe && (
                  <button
                    type="button"
                    className="remember-clear"
                    onClick={() => {
                      localStorage.removeItem(REMEMBER_KEY);
                      setRememberMe(false);
                      setUsername('');
                      setPassword('');
                    }}
                  >
                    清除
                  </button>
                )}
              </div>
            )}

            <button type="submit" className="login-submit" disabled={loading || wechatLoading}>
              {loading ? <span className="login-spinner" /> : null}
              {loading ? '处理中...' : (isRegister ? '注册' : '登录')}
            </button>
          </form>

          {!isRegister && <WeChatLogin disabled={loading} onBusyChange={setWechatLoading}
            onSuccess={data => loginWithTokenAndUser(data.token, data.user)} />}

          <div className="login-switch">
            <span>{isRegister ? '已有账号？' : '没有账号？'}</span>
            <button onClick={() => { setIsRegister(!isRegister); setError(''); }}>
              {isRegister ? '去登录' : '注册'}
            </button>
          </div>

          <div className="login-apk-panel">
            <div className="login-apk-copy">
              <strong>Android APK</strong>
              <span>在其他手机上扫码安装小本本</span>
            </div>
            <button
              type="button"
              className="login-apk-btn"
              onClick={handleOpenApkDownload}
              disabled={apkDownloadBusy}
            >
              {apkDownloadBusy ? '打开中...' : '扫码下载'}
            </button>
          </div>
          {apkDownloadMessage && <div className="login-apk-status">{apkDownloadMessage}</div>}

          <nav aria-label="产品与法律信息" className="login-legal-links">
            <a href="/about.html" target="_blank" rel="noreferrer">产品介绍</a>
            <a href="/terms.html" target="_blank" rel="noreferrer">用户协议</a>
            <a href="/privacy.html" target="_blank" rel="noreferrer">隐私政策</a>
          </nav>
          {/* 开发者入口已隐藏 */}
        </div>
      </div>

      <style>{`
        .login-legal-links { display: flex; justify-content: center; flex-wrap: wrap; gap: 12px 20px; margin-top: 24px; font-size: 13px; }
        .login-legal-links a { color: #d6c6f5; text-underline-offset: 4px; }
        .login-legal-links a:focus-visible { outline: 2px solid #c4b5fd; outline-offset: 4px; }

        .login-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a0118 0%, #0d0520 50%, #080112 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        /* 星尘粒子 */
        .login-stars {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 0;
        }
        .star {
          position: absolute;
          background: white;
          border-radius: 50%;
          animation: twinkle var(--dur, 3s) ease-in-out infinite;
          opacity: 0;
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 0.7; transform: scale(1); }
        }

        /* 背景光球 */
        .login-bg-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
        }
        .orb-1 {
          width: 350px; height: 350px;
          background: radial-gradient(circle, rgba(109,40,217,0.4) 0%, transparent 70%);
          top: -100px; right: -80px;
          animation: breathe 5s ease-in-out infinite;
        }
        .orb-2 {
          width: 300px; height: 300px;
          background: radial-gradient(circle, rgba(192,38,211,0.3) 0%, transparent 70%);
          bottom: -80px; left: -60px;
          animation: breathe 6s ease-in-out infinite 1.5s;
        }
        .orb-3 {
          width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(67,56,202,0.35) 0%, transparent 70%);
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          animation: breathe 7s ease-in-out infinite 0.8s;
        }

        @keyframes breathe {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.15); opacity: 1; }
        }

        .login-container {
          width: 100%;
          max-width: 400px;
          position: relative;
          z-index: 10;
          animation: slideUp 500ms var(--ease-spring, cubic-bezier(0.34,1.56,0.64,1));
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Logo */
        .login-logo {
          text-align: center;
          margin-bottom: 28px;
        }
        .login-logo-icon {
          margin-bottom: 14px;
          filter: drop-shadow(0 0 24px rgba(139, 92, 246, 0.35));
          animation: iconFloat 2.8s ease-in-out infinite;
        }
        @keyframes iconFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          35% { transform: translate(3px, -8px) rotate(3deg); }
          70% { transform: translate(-2px, -3px) rotate(-2deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-logo-icon { animation: none; }
        }
        .login-brand {
          font-size: 28px;
          font-weight: 800;
          background: linear-gradient(135deg, #c084fc, #818cf8, #f0abfc);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
          letter-spacing: 3px;
        }
        .login-tagline {
          font-size: 12px;
          color: rgba(167, 139, 250, 0.6);
          margin-top: 5px;
          letter-spacing: 1px;
        }

        /* Card */
        .login-card {
          background: rgba(15, 5, 30, 0.85);
          border: 1px solid rgba(139, 92, 246, 0.25);
          border-radius: 20px;
          padding: 32px 28px;
          box-shadow: 0 8px 40px rgba(109, 40, 217, 0.3), inset 0 1px 0 rgba(167, 139, 250, 0.1);
          backdrop-filter: blur(20px);
        }
        .login-title {
          font-size: 22px;
          font-weight: 700;
          color: #e9d5ff;
          margin: 0 0 4px;
        }
        .login-desc {
          font-size: 13px;
          color: rgba(167, 139, 250, 0.6);
          margin: 0 0 22px;
        }

        /* Error */
        .login-error {
          background: rgba(239, 68, 68, 0.1);
          color: #fca5a5;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          margin-bottom: 16px;
          border: 1px solid rgba(239, 68, 68, 0.25);
        }

        /* Fields */
        .login-field {
          margin-bottom: 16px;
        }
        .login-field label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          color: rgba(167, 139, 250, 0.7);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .login-field .optional {
          text-transform: none;
          font-weight: 400;
          color: rgba(167, 139, 250, 0.4);
        }
        .login-field input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.04);
          border: 1.5px solid rgba(139, 92, 246, 0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: inherit;
          color: #e9d5ff;
          transition: border-color 150ms, box-shadow 150ms, background 150ms;
          box-sizing: border-box;
        }
        .login-field input:focus {
          outline: none;
          border-color: rgba(139, 92, 246, 0.7);
          box-shadow: 0 0 0 3px rgba(109, 40, 217, 0.2);
          background: rgba(255, 255, 255, 0.06);
        }
        .login-field input::placeholder {
          color: rgba(167, 139, 250, 0.3);
        }

        /* 记住密码 */
        .login-remember {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          margin-top: -4px;
        }
        .remember-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }
        .remember-checkbox {
          display: none;
        }
        .remember-custom-box {
          width: 18px;
          height: 18px;
          border-radius: 5px;
          border: 1.5px solid rgba(139, 92, 246, 0.5);
          background: rgba(109, 40, 217, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 200ms;
          flex-shrink: 0;
        }
        .remember-label:hover .remember-custom-box {
          border-color: rgba(139, 92, 246, 0.8);
          background: rgba(109, 40, 217, 0.25);
        }
        .remember-checkbox:checked + .remember-custom-box {
          background: linear-gradient(135deg, #7c3aed, #a855f7);
          border-color: #8b5cf6;
          box-shadow: 0 0 8px rgba(139, 92, 246, 0.5);
        }
        .remember-text {
          font-size: 13px;
          color: rgba(167, 139, 250, 0.7);
        }
        .remember-clear {
          background: none;
          border: none;
          color: rgba(167, 139, 250, 0.5);
          font-size: 12px;
          cursor: pointer;
          font-family: inherit;
          padding: 2px 6px;
          border-radius: 4px;
          transition: all 150ms;
        }
        .remember-clear:hover {
          color: #fca5a5;
          background: rgba(239, 68, 68, 0.1);
        }

        /* Submit */
        .login-submit {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #6d28d9, #7c3aed, #a21caf);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 700;
          font-family: inherit;
          cursor: pointer;
          margin-top: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 200ms;
          box-shadow: 0 4px 20px rgba(109, 40, 217, 0.5);
          letter-spacing: 0.05em;
          position: relative;
          overflow: hidden;
        }
        .login-submit::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent);
          opacity: 0;
          transition: opacity 200ms;
        }
        .login-submit:hover {
          box-shadow: 0 6px 30px rgba(109, 40, 217, 0.7);
          transform: translateY(-1px);
        }
        .login-submit:hover::before {
          opacity: 1;
        }
        .login-submit:active {
          transform: scale(0.97) translateY(0);
        }
        .login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .login-spinner {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Switch */
        .login-switch {
          text-align: center;
          margin-top: 20px;
          font-size: 14px;
          color: rgba(167, 139, 250, 0.5);
        }
        .login-switch button {
          background: none;
          border: none;
          color: #a78bfa;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          margin-left: 4px;
        }
        .login-switch button:hover {
          color: #c084fc;
          text-decoration: underline;
        }

        .login-apk-panel {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-top: 18px;
          padding: 12px;
          border: 1px solid rgba(139, 92, 246, 0.22);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.035);
        }
        .login-apk-copy {
          display: flex;
          flex-direction: column;
          gap: 3px;
          min-width: 0;
        }
        .login-apk-copy strong {
          color: #e9d5ff;
          font-size: 13px;
          font-weight: 700;
        }
        .login-apk-copy span {
          color: rgba(167, 139, 250, 0.58);
          font-size: 12px;
          line-height: 1.35;
        }
        .login-apk-btn {
          flex: 0 0 auto;
          min-width: 88px;
          padding: 9px 12px;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #22c55e, #38bdf8);
          color: #07111f;
          font-family: inherit;
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          transition: transform 150ms, opacity 150ms;
        }
        .login-apk-btn:active { transform: scale(0.97); }
        .login-apk-btn:disabled { opacity: 0.65; cursor: wait; }
        .login-apk-status {
          margin-top: 8px;
          color: #fca5a5;
          font-size: 12px;
          line-height: 1.45;
        }

        .login-dev {
          margin-top: 14px;
          display: flex;
          justify-content: center;
        }
        .login-dev-btn {
          background: transparent;
          border: 1px dashed rgba(139, 92, 246, 0.2);
          color: rgba(167, 139, 250, 0.4);
          padding: 8px 12px;
          border-radius: 10px;
          font-size: 12px;
          cursor: pointer;
          transition: all 150ms;
          font-family: inherit;
        }
        .login-dev-btn:hover {
          color: rgba(167, 139, 250, 0.7);
          border-color: rgba(139, 92, 246, 0.4);
          background: rgba(109, 40, 217, 0.08);
        }
        .login-dev-btn:active {
          transform: scale(0.98);
        }

        /* 分割线 */
        .login-divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 20px 0 16px;
        }
        .login-divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent);
        }
        .login-divider-text {
          font-size: 12px;
          color: rgba(167, 139, 250, 0.4);
          white-space: nowrap;
          letter-spacing: 0.05em;
        }

        /* 微信登录按钮 */
        .login-wechat-btn {
          width: 100%;
          padding: 12px 14px;
          background: rgba(7, 193, 96, 0.08);
          border: 1.5px solid rgba(7, 193, 96, 0.3);
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          font-family: inherit;
          color: #07C160;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 200ms;
          position: relative;
          overflow: hidden;
        }
        .login-wechat-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(7, 193, 96, 0.05), transparent);
          opacity: 0;
          transition: opacity 200ms;
        }
        .login-wechat-btn:hover {
          background: rgba(7, 193, 96, 0.15);
          border-color: rgba(7, 193, 96, 0.5);
          box-shadow: 0 4px 20px rgba(7, 193, 96, 0.2);
          transform: translateY(-1px);
        }
        .login-wechat-btn:hover::before {
          opacity: 1;
        }
        .login-wechat-btn:active {
          transform: scale(0.97) translateY(0);
        }
        .login-wechat-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        .wechat-icon {
          flex-shrink: 0;
        }
        .wechat-spinner {
          border-color: rgba(7, 193, 96, 0.3);
          border-top-color: #07C160;
          width: 16px;
          height: 16px;
        }

        /* 小程序提示卡片 */
        .login-wechat-miniapp-hint {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          background: rgba(7, 193, 96, 0.06);
          border: 1px solid rgba(7, 193, 96, 0.2);
          border-radius: 12px;
        }
        .login-wechat-miniapp-hint .wechat-icon {
          margin-top: 2px;
          flex-shrink: 0;
        }
        .miniapp-hint-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .miniapp-hint-title {
          font-size: 14px;
          font-weight: 600;
          color: #07C160;
        }
        .miniapp-hint-desc {
          font-size: 12px;
          color: rgba(7, 193, 96, 0.6);
          line-height: 1.5;
        }

        /* ========== 扫码登录弹窗 ========== */
        .qr-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 200ms ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .qr-modal {
          background: rgba(15, 5, 30, 0.95);
          border: 1px solid rgba(7, 193, 96, 0.3);
          border-radius: 20px;
          padding: 32px 28px;
          width: 340px;
          max-width: 90vw;
          position: relative;
          box-shadow: 0 8px 40px rgba(7, 193, 96, 0.15), 0 0 80px rgba(109, 40, 217, 0.2);
          animation: modalSlideUp 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .qr-modal-close {
          position: absolute;
          top: 12px;
          right: 14px;
          background: none;
          border: none;
          color: rgba(167, 139, 250, 0.5);
          font-size: 18px;
          cursor: pointer;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 150ms;
        }
        .qr-modal-close:hover {
          color: #e9d5ff;
          background: rgba(139, 92, 246, 0.15);
        }
        .qr-modal-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 24px;
          justify-content: center;
        }
        .qr-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: #07C160;
        }
        .qr-modal-body {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          min-height: 260px;
          justify-content: center;
        }
        .qr-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }
        .qr-loading p {
          color: rgba(167, 139, 250, 0.6);
          font-size: 14px;
          margin: 0;
        }
        .qr-image-wrapper {
          background: white;
          border-radius: 12px;
          padding: 12px;
          margin-bottom: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        .qr-image {
          width: 200px;
          height: 200px;
          display: block;
        }
        .qr-hint {
          color: #e9d5ff;
          font-size: 15px;
          font-weight: 600;
          margin: 0 0 4px;
        }
        .qr-hint-sub {
          color: rgba(167, 139, 250, 0.5);
          font-size: 12px;
          margin: 0 0 8px;
        }
        .qr-countdown {
          color: rgba(167, 139, 250, 0.4);
          font-size: 12px;
          margin: 4px 0 0;
          font-variant-numeric: tabular-nums;
        }
        .qr-scanned, .qr-confirmed, .qr-expired, .qr-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        .qr-scanned p, .qr-confirmed p, .qr-expired p, .qr-error p {
          color: #e9d5ff;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }
        .qr-scanned-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: rgba(7, 193, 96, 0.15);
          border: 2px solid #07C160;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: #07C160;
          margin-bottom: 8px;
          animation: pulse 1.5s ease-in-out infinite;
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(7, 193, 96, 0.3); }
          50% { box-shadow: 0 0 0 12px rgba(7, 193, 96, 0); }
        }
        .qr-confirmed-icon {
          font-size: 48px;
          margin-bottom: 8px;
        }
        .qr-refresh-btn {
          margin-top: 12px;
          padding: 10px 24px;
          background: rgba(7, 193, 96, 0.12);
          border: 1.5px solid rgba(7, 193, 96, 0.4);
          border-radius: 10px;
          color: #07C160;
          font-size: 14px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 200ms;
        }
        .qr-refresh-btn:hover {
          background: rgba(7, 193, 96, 0.2);
          border-color: rgba(7, 193, 96, 0.6);
          box-shadow: 0 2px 12px rgba(7, 193, 96, 0.2);
        }
        .qr-refresh-btn:active {
          transform: scale(0.96);
        }
      `}</style>
    </div>
  );
};

export default Login;
